"use client";

import type { Address } from "@/types/prisma";
import { Check, CreditCard, Loader2, MapPin, Package, ShieldCheck, Truck } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useSession } from "next-auth/react";
import { FormEvent, useEffect, useState } from "react";
import { toast } from "sonner";

import { useCartStore } from "@/components/providers/cart-store";
import { Button } from "@/components/ui/button";
import { calculateShipping } from "@/lib/delivery";
import { formatPrice } from "@/lib/utils";
import type { CheckoutStep } from "@/types";

const steps: { id: CheckoutStep; label: string; icon: typeof MapPin }[] = [
  { id: "address", label: "Address", icon: MapPin },
  { id: "delivery", label: "Delivery", icon: Truck },
  { id: "payment", label: "Payment", icon: CreditCard },
  { id: "review", label: "Review", icon: Package }
];

export function CheckoutForm() {
  const [mounted, setMounted] = useState(false);
  const { data: session, status } = useSession();
  const items = useCartStore((s) => s.items);
  const subtotal = useCartStore((s) => s.subtotal());
  const couponDiscount = useCartStore((s) => s.couponDiscount);
  const [step, setStep] = useState<CheckoutStep>("address");
  const [loading, setLoading] = useState(false);
  const [deliveryMethod, setDeliveryMethod] = useState("standard");
  const [paymentMethod, setPaymentMethod] = useState("cod");
  const [address, setAddress] = useState({
    fullName: "",
    phone: "",
    line1: "",
    line2: ""
  });
  const [savedAddresses, setSavedAddresses] = useState<Address[]>([]);
  const [selectedAddressId, setSelectedAddressId] = useState<string | null>(null);
  const [addressErrors, setAddressErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!session?.user) return;

    let cancelled = false;
    (async () => {
      try {
        const response = await fetch("/api/addresses");
        if (!response.ok) return;
        const data = (await response.json()) as { addresses: Address[] };
        if (cancelled) return;

        setSavedAddresses(data.addresses);
        const preferred = data.addresses.find((a) => a.isDefault) ?? data.addresses[0];
        if (preferred) {
          setSelectedAddressId(preferred.id);
          setAddress({
            fullName: preferred.fullName,
            phone: preferred.phone,
            line1: preferred.line1,
            line2: preferred.line2 ?? ""
          });
        }
      } catch {
        // saved addresses are a convenience; manual entry still works
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [session?.user]);

  function selectSavedAddress(saved: Address) {
    setSelectedAddressId(saved.id);
    setAddressErrors({});
    setAddress({
      fullName: saved.fullName,
      phone: saved.phone,
      line1: saved.line1,
      line2: saved.line2 ?? ""
    });
  }

  function validateAddress(): boolean {
    const errors: Record<string, string> = {};
    if (!address.fullName.trim()) {
      errors.fullName = "Full name is required";
    } else if (address.fullName.trim().length < 2) {
      errors.fullName = "Full name must be at least 2 characters";
    }

    if (!address.phone.trim()) {
      errors.phone = "Mobile phone number is required";
    } else if (address.phone.trim().replace(/\s+/g, "").length < 8) {
      errors.phone = "Mobile number must be at least 8 digits";
    }

    if (!address.line1.trim()) {
      errors.line1 = "Address line 1 is required";
    } else if (address.line1.trim().length < 4) {
      errors.line1 = "Address line 1 must be at least 4 characters";
    }

    setAddressErrors(errors);
    if (Object.keys(errors).length > 0) {
      toast.error("Please fill in all required delivery address fields.");
      return false;
    }
    return true;
  }

  function handleContinueToPayment(event?: FormEvent) {
    if (event) event.preventDefault();
    if (!validateAddress()) return;
    setStep("payment");
  }

  const shipping = calculateShipping(subtotal, true);
  const total = subtotal - couponDiscount + shipping;

  const stepIndex = steps.findIndex((s) => s.id === step);

  async function placeOrder(event: FormEvent) {
    event.preventDefault();
    if (!session?.user) {
      toast.error("Please sign in before checkout.");
      return;
    }
    if (!validateAddress()) {
      setStep("address");
      return;
    }
    setLoading(true);
    try {
      const response = await fetch("/api/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items: items.map((item) => ({ productId: item.product.id, quantity: item.quantity })),
          address,
          deliveryMethod,
          paymentMethod,
          couponCode: useCartStore.getState().couponCode
        })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message ?? "Checkout failed");
      if (data.url) window.location.href = data.url;
      else if (data.orderId) window.location.href = `/checkout/success?orderId=${data.orderId}`;
      else window.location.href = "/checkout/success";
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Checkout failed");
    } finally {
      setLoading(false);
    }
  }



  if (!mounted) {
    return (
      <div className="mx-auto max-w-3xl px-5 py-16 text-center">
        <div className="mx-auto h-8 w-48 animate-pulse rounded bg-slate-200 dark:bg-white/10" />
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="mx-auto max-w-3xl px-5 py-16 text-center">
        <h1 className="text-3xl font-bold">No items to checkout</h1>
        <Link href="/search" className="amazon-btn-primary mt-6 inline-flex">
          Continue shopping
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-5">
      <nav className="mb-6 flex items-center gap-1.5 overflow-x-auto pb-2 no-scrollbar sm:flex-wrap sm:gap-2 sm:pb-0">
        {steps.map((s, i) => {
          const Icon = s.icon;
          const done = i < stepIndex;
          const current = s.id === step;
          return (
            <div key={s.id} className="flex shrink-0 items-center gap-1.5 sm:gap-2">
              <div
                className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-bold sm:px-3 sm:py-1.5 sm:text-sm ${
                  current ? "bg-amazon-orange text-slate-950" : done ? "bg-amazon-green/20 text-amazon-green" : "bg-slate-200 text-slate-600 dark:bg-white/10"
                }`}
              >
                {done ? <Check className="h-3.5 w-3.5 sm:h-4 sm:w-4" /> : <Icon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />}
                {s.label}
              </div>
              {i < steps.length - 1 && <span className="text-xs text-slate-300 sm:text-sm">→</span>}
            </div>
          );
        })}
      </nav>

      <div className="grid gap-6 lg:grid-cols-[1fr_380px]">
        <div className="amazon-card">
          {status !== "loading" && !session?.user && (
            <div className="mb-5 rounded border border-amber-200 bg-amber-50 p-4 text-sm">
              <Link href="/login?callbackUrl=/checkout" className="font-bold text-amazon-teal underline">
                Sign in
              </Link>{" "}
              to place your order.
            </div>
          )}

          {step === "address" && (
            <div>
              <h1 className="text-2xl font-bold">Delivery address</h1>

              {savedAddresses.length > 0 && (
                <div className="mt-4">
                  <label className="flex cursor-pointer items-start gap-3 rounded border border-amazon-orange bg-amber-50/50 p-3 text-sm dark:border-amazon-orange/50 dark:bg-amber-950/20">
                    <input
                      type="radio"
                      name="savedAddress"
                      className="mt-1"
                      checked={selectedAddressId === savedAddresses[0].id}
                      onChange={() => selectSavedAddress(savedAddresses[0])}
                      defaultChecked
                    />
                    <span>
                      <span className="block font-bold">{savedAddresses[0].fullName}</span>
                      <span className="text-slate-600 dark:text-slate-300">
                        {savedAddresses[0].line1}
                        {savedAddresses[0].line2 ? `, ${savedAddresses[0].line2}` : ""}
                      </span>
                      <span className="mt-0.5 block text-xs text-slate-500">{savedAddresses[0].phone}</span>
                    </span>
                  </label>
                </div>
              )}

              {/* Manual entry (shown when no saved address or user wants to type a different one) */}
              <form onSubmit={handleContinueToPayment}>
                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                  {(
                    [
                      ["fullName", "Full name", false, "e.g. Abebe Kebede", true],
                      ["phone", "Mobile", false, "+251 91 234 5678", true],
                      ["line1", "Address line 1", true, "Street address, P.O. Box, Company name", true],
                      ["line2", "Address line 2 (optional)", true, "Apartment, suite, unit, building, floor, etc.", false]
                    ] as [keyof typeof address, string, boolean, string, boolean][]
                  ).map(([key, label, full, placeholder, isRequired]) => (
                    <label key={key} className={full ? "sm:col-span-2" : ""}>
                      <span className="flex items-center text-sm font-bold text-slate-900 dark:text-white">
                        {label}
                        {isRequired && <span className="ml-1 text-red-500 font-bold">*</span>}
                      </span>
                      <input
                        required={isRequired}
                        placeholder={placeholder}
                        value={address[key]}
                        onChange={(e) => {
                          setSelectedAddressId(null);
                          setAddress((a) => ({ ...a, [key]: e.target.value }));
                          if (addressErrors[key]) {
                            setAddressErrors((errs) => {
                              const updated = { ...errs };
                              delete updated[key];
                              return updated;
                            });
                          }
                        }}
                        className={`mt-1 h-10 w-full rounded border px-3 text-sm outline-none transition-colors dark:bg-slate-950 ${
                          addressErrors[key]
                            ? "border-red-500 focus:border-red-500 ring-1 ring-red-500"
                            : "border-slate-200 focus:border-amazon-orange dark:border-white/10"
                        }`}
                      />
                      {addressErrors[key] && (
                        <p className="mt-1 text-xs font-semibold text-red-500">
                          {addressErrors[key]}
                        </p>
                      )}
                    </label>
                  ))}
                </div>
                <Button type="submit" className="mt-6">
                  Continue to Payment
                </Button>
              </form>
            </div>
          )}

          {step === "payment" && (
            <div>
              <h1 className="text-2xl font-bold">Payment method</h1>
              <p className="mt-1 text-sm text-slate-500">Cash on delivery available. Digital and mobile banking options coming soon.</p>
              <div className="mt-4 space-y-3">
                {[
                  { id: "cod", label: "Cash on Delivery", disabled: false },
                  { id: "telebirr", label: "Telebirr", disabled: true },
                  { id: "cbe", label: "Commercial Bank of Ethiopia (CBE / CBE Birr)", disabled: true }
                ].map((opt) => (
                  <label
                    key={opt.id}
                    className={`flex items-center gap-3 rounded border p-4 ${
                      paymentMethod === opt.id ? "border-amazon-orange bg-amber-50/50 dark:bg-amber-950/20" : "border-slate-200 dark:border-white/10"
                    } ${opt.disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
                  >
                    <input
                      type="radio"
                      name="payment"
                      disabled={opt.disabled}
                      checked={paymentMethod === opt.id}
                      onChange={() => setPaymentMethod(opt.id)}
                    />
                    <span className="font-bold">{opt.label}</span>
                    {opt.disabled && <span className="text-xs text-slate-400 font-medium">(Coming soon)</span>}
                  </label>
                ))}
              </div>
              <div className="mt-6 flex gap-3">
                <Button variant="outline" onClick={() => setStep("address")}>
                  Back
                </Button>
                <Button onClick={() => setStep("review")}>Review order</Button>
              </div>
            </div>
          )}


          {step === "review" && (
            <form onSubmit={placeOrder}>
              <h1 className="text-2xl font-bold">Review your order</h1>
              <div className="mt-4 rounded border border-slate-200 p-4 text-sm dark:border-white/10">
                <p className="font-bold">Deliver to</p>
                <p className="mt-1 text-slate-600 dark:text-slate-300">
                  {address.fullName}, {address.line1}
                  {address.line2 ? `, ${address.line2}` : ""}
                </p>
                <p className="mt-2">
                  <strong>Payment:</strong> {paymentMethod === "cod" ? "Cash on Delivery (COD)" : paymentMethod}
                </p>
              </div>
              <div className="mt-6 flex gap-3">
                <Button type="button" variant="outline" onClick={() => setStep("payment")}>
                  Back
                </Button>
                <Button type="submit" disabled={loading || !session?.user}>
                  {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                  Place order
                </Button>
              </div>
            </form>
          )}
        </div>

        <aside className="amazon-card h-fit">
          <h2 className="text-xl font-bold">Order summary</h2>
          <div className="mt-4 max-h-48 space-y-3 overflow-y-auto">
            {items.map((item) => (
              <div key={item.product.id} className="flex gap-3">
                <div className="relative h-14 w-14 shrink-0 overflow-hidden rounded bg-slate-100">
                  <Image src={item.product.images[0]} alt="" fill sizes="56px" className="object-cover" />
                </div>
                <div className="min-w-0 flex-1 text-sm">
                  <p className="line-clamp-2 font-medium">{item.product.title}</p>
                  <p className="text-slate-500">Qty {item.quantity}</p>
                </div>
                <strong className="text-sm">{formatPrice(item.product.price * item.quantity)}</strong>
              </div>
            ))}
          </div>

          <div className="mt-4 space-y-2 border-t pt-4 text-sm">
            <div className="flex justify-between">
              <span>Subtotal</span>
              <strong>{formatPrice(subtotal)}</strong>
            </div>
            {couponDiscount > 0 && (
              <div className="flex justify-between text-amazon-green">
                <span>Coupon</span>
                <strong>-{formatPrice(couponDiscount)}</strong>
              </div>
            )}
            <div className="flex justify-between">
              <span>Shipping</span>
              <strong>{shipping === 0 ? "FREE" : formatPrice(shipping)}</strong>
            </div>
            <div className="flex justify-between text-base font-bold">
              <span>Total</span>
              <span className="text-amazon-orange">{formatPrice(total)}</span>
            </div>
          </div>
          <p className="mt-4 flex gap-2 rounded bg-emerald-50 p-3 text-xs text-amazon-green dark:bg-emerald-950/30">
            <ShieldCheck className="h-4 w-4 shrink-0" />
            Secure checkout. Payment providers ready for production.
          </p>
        </aside>
      </div>
    </div>
  );
}
